import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { Icon, type IconName } from '../Icon.js';

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /** Why it is disabled, said beside it rather than hidden. */
  readonly hint?: string;
  readonly tone?: 'danger';
  readonly testId?: string;
  /** A glyph before the label, for a menu whose items are easier told apart by shape. */
  readonly icon?: IconName;
  /** A rule above this item: it starts a group (the destructive action, usually). */
  readonly separatorBefore?: boolean;
}

/**
 * THE SECONDARY ACTIONS, ONE CLICK AWAY.
 *
 * Everything a screen can do stays reachable; what is not the main task moves
 * here instead of standing beside it as one more button. A menu button in the
 * WAI-ARIA sense: Escape closes it and returns focus, the arrows move between
 * items, a click outside dismisses it, and Tab closes it and moves on, as does
 * focus landing anywhere outside it.
 */
export function MoreMenu({
  items,
  label = 'More actions',
  buttonLabel,
  testId,
  align = 'end',
  buttonClassName = 'ctl',
  buttonIcon = 'more',
}: {
  items: readonly MenuItem[];
  label?: string;
  /** Text on the button; the three dots alone when absent. */
  buttonLabel?: string;
  testId: string;
  align?: 'start' | 'end';
  /** The button's own look — the caret of a split button wears its button's. */
  buttonClassName?: string;
  /** The glyph on a button without text. */
  buttonIcon?: IconName;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const close = (event: Event) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    // Focus arriving outside the menu by any route (a screen reader, a
    // programmatic focus) leaves it open over the page otherwise. A click on an
    // item in Safari focuses nothing, so this never closes the menu under it.
    document.addEventListener('focusin', close);
    // Focus the first live item as the menu opens.
    root.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus();
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('focusin', close);
    };
  }, [open]);

  if (items.length === 0) return null;

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
      return;
    }
    if (event.key === 'Tab') {
      // The items are out of the tab order, so Tab leaves the menu: close it
      // and let the browser move on from the button, forwards or back. Focus
      // goes to the button first because the focused item is about to unmount.
      if (!open) return;
      setOpen(false);
      button.current?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const all = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const at = all.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? (at + 1) % all.length : (at - 1 + all.length) % all.length;
    all[next]?.focus();
  };

  return (
    <div className="ui-menu" ref={root} onKeyDown={move}>
      <button
        ref={button}
        type="button"
        className={`${buttonClassName} ui-menu-button`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={buttonLabel === undefined ? label : undefined}
        data-testid={testId}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {buttonLabel === undefined ? <Icon name={buttonIcon} size={18} strokeWidth={buttonIcon === 'more' ? 2.6 : 2} /> : buttonLabel}
        {buttonLabel === undefined ? null : <Icon name="chevronDown" size={14} />}
      </button>
      {open ? (
        <div className="ui-menu-list" role="menu" id={menuId} aria-label={label} data-align={align} data-testid={`${testId}-menu`}>
          {items.map((item) => (
            <Fragment key={item.id}>
              {item.separatorBefore === true ? <div className="ui-menu-sep" role="separator" /> : null}
              <button
                type="button"
                role="menuitem"
                className="ui-menu-item"
                data-tone={item.tone}
                data-icon={item.icon === undefined ? undefined : '1'}
                aria-disabled={item.disabled === true}
                data-testid={item.testId}
                tabIndex={-1}
                onClick={() => {
                  if (item.disabled === true) return;
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {/* The glyph is the svg itself, never a span: the label stays the item's first span. */}
                {item.icon === undefined ? null : <Icon name={item.icon} size={16} />}
                <span>{item.label}</span>
                {item.hint === undefined ? null : <span className="ui-menu-hint">{item.hint}</span>}
              </button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
